import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Badge, Button, DataTable, Empty, FormActions, FormDrawer, FormSection, IconButton, InfoBar, Input, Modal, PageHeader, SearchInput, Spin, TableStatusBar, useAlert } from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import { KeyRound, Pencil, Plus, Save, Users } from 'lucide-react';

/** Renvoie la première clé non vide parmi les candidates. */
function pick(row: any, ...keys: string[]) {
    if (!row) return undefined;
    for (const k of keys) {
        if (row[k] != null && row[k] !== '') return row[k];
    }
    return undefined;
}

/** Badge de statut client : tolère les formats numériques (1/0) et textuels. */
function StatutClient({ value }: { value: any }) {
    if (value == null || value === '') return <span>—</span>;
    const s = String(value).toUpperCase();
    const actif = s === '1' || s === 'ACTIF' || s === 'TRUE' || s === 'OUI';
    const bloque = s === '0' || s === '2' || s.includes('BLOQ') || s.includes('INACT') || s === 'FALSE';
    return (
        <Badge color={actif ? 'blue' : bloque ? 'red' : 'gray'}>
            {actif ? 'actif' : bloque ? 'bloqué' : String(value)}
        </Badge>
    );
}

/**
 * Formulaire de création / modification d'un client (dans le FormDrawer).
 * CREATE → POST api/Clients/AddClient ; UPDATE → PUT api/Clients/UpdateClient.
 */
const FormClient = (props: { client?: any | null; onSaved: () => void }) => {

    // USER TOKEN
    const authHeader = useAuthHeader()
    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const { register, handleSubmit, setValue, reset } = useForm()
    const [loading, setLoading] = useState<boolean>(false)

    const isEdit = !!props.client

    // Pré-remplissage (UPDATE) ou remise à zéro (CREATE)
    useEffect(() => {
        if (props.client) {
            setValue('Nom', pick(props.client, 'Nom') || '')
            setValue('Prenom', pick(props.client, 'Prenom') || '')
            setValue('Telephone', pick(props.client, 'Telephone', 'Phone', 'Mobile') || '')
            setValue('Email', pick(props.client, 'Email', 'Mail') || '')
            setValue('NumeroIdentite', pick(props.client, 'NumeroIdentite', 'CNI', 'NumeroPiece') || '')
        } else {
            reset()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.client])

    const onSubmit = async (data: any) => {
        const payload: any = isEdit
            ? {
                CodeClient: pick(props.client, 'CodeClient', 'Id'),
                Nom: data.Nom,
                Prenom: data.Prenom,
                Telephone: data.Telephone,
                Email: data.Email || null,
                NumeroIdentite: data.NumeroIdentite || null,
            }
            : {
                Nom: data.Nom,
                Prenom: data.Prenom,
                Telephone: data.Telephone,
                Password: data.Password || null,
                Email: data.Email || null,
                NumeroIdentite: data.NumeroIdentite || null,
                CodeAbonne: user.CodeAbonne,
                CreatedBy: user.CodeU,
            }

        setLoading(true)
        let config = {
            method: isEdit ? 'put' : 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + (isEdit ? datas.API_LINKs.Clients.UpdateClient : datas.API_LINKs.Clients.AddClient),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    reset()
                    props.onSaved()
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error)
                console.log(error);
            })
    }

    return (
        <Spin spinning={loading}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormSection title="Identité" subtitle="Informations personnelles du client">
                    <Input
                        {...register("Nom")}
                        required
                        label={'Nom'}
                        placeholder={"Nom de famille"}
                    />
                    <Input
                        {...register("Prenom")}
                        required
                        label={'Prénom'}
                        placeholder={"Prénom"}
                    />
                    <Input
                        {...register("Telephone")}
                        required
                        label={'Téléphone'}
                        placeholder={"Ex : 0700000000"}
                    />
                    <Input
                        {...register("NumeroIdentite")}
                        label={"N° pièce d'identité"}
                        placeholder={"Numéro de la pièce d'identité"}
                    />
                </FormSection>

                <FormSection title="Contact & accès" subtitle="Adresse e-mail et mot de passe éventuel">
                    <Input
                        {...register("Email")}
                        type='email'
                        label={'E-mail'}
                        placeholder={"adresse@exemple.com"}
                    />
                    {!isEdit && (
                        <Input
                            {...register("Password")}
                            type='password'
                            label={'Mot de passe'}
                            placeholder={"Laisser vide pour un mot de passe généré"}
                            helperText={"Optionnel — le client pourra le réinitialiser."}
                        />
                    )}
                </FormSection>

                <FormActions>
                    <Button variant='secondary' type='submit' startIcon={<Save size={16} />} loading={loading}>
                        {isEdit ? 'Mettre à jour' : 'Enregistrer'}
                    </Button>
                </FormActions>
            </form>
        </Spin>
    );
};

export default function GestionClients() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [data, setData] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [critere, setCritere] = useState('')
    const [searched, setSearched] = useState(false)
    const [searchRequired, setSearchRequired] = useState(false)

    // Drawer création / édition
    const [open, setOpen] = useState(false)
    const [client, setClient] = useState<any>(null)

    // Confirmation reset mot de passe
    const [confirmReset, setConfirmReset] = useState<{ open: boolean; row: any | null }>({ open: false, row: null })
    const [actionLoading, setActionLoading] = useState(false)

    // Ligne sélectionnée (InfoBar)
    const [selected, setSelected] = useState<any>()

    useEffect(() => {
        // Chargement initial : tentative de listing large (critère vide).
        // Si le backend exige un critère, l'état vide de recherche est affiché.
        SearchClient('', true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Recherche des clients via GET api/Clients/GetClient (critère chiffré). */
    function SearchClient(search: string, silent = false) {
        setCritere(search)
        setLoading(true)
        const payload = {
            Critere: search,
            CodeAbonne: user.CodeAbonne
        }
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Clients.GetClient + '?CipherClient=' + Crypter.encrypt(JSON.stringify(payload)),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    // Tolère un tableau ou un objet unique
                    const list = Array.isArray(res.Result) ? res.Result : (res.Result ? [res.Result] : [])
                    setData(list.map((c: any, i: number) => ({ id: pick(c, 'CodeClient', 'Id') ?? i, ...c })))
                    setSearched(true)
                    setSearchRequired(false)
                }
                else {
                    setData([])
                    setSearched(true)
                    if (silent) setSearchRequired(true)
                    else alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                setData([])
                setSearched(true)
                if (silent) setSearchRequired(true)
                else alert.error('Error', error)
                console.log(error);
            });
    }

    /** Réinitialisation du mot de passe par le back-office (POST api/Clients/ResetPassword). */
    function ResetPassword() {
        const row = confirmReset.row
        if (!row) return
        setActionLoading(true)
        const payload = {
            CodeClient: pick(row, 'CodeClient', 'Id'),
            Telephone: pick(row, 'Telephone', 'Phone', 'Mobile') ?? null,
            CodeAbonne: user.CodeAbonne,
        }
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Clients.ResetPassword,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                setActionLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    setConfirmReset({ open: false, row: null })
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setActionLoading(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    function openEdit(row: any) {
        setClient(row)
        setOpen(true)
    }

    function openCreate() {
        setClient(null)
        setOpen(true)
    }

    const hasStatut = data.some((r: any) => r.Statut != null || r.IsActif != null)

    const columns: DataTableColumn[] =
        [
            {
                field: 'CodeClient', headerName: 'Code client', width: 110,
                renderCell: ({ value, row }) => <span>{value ?? pick(row, 'Id') ?? '—'}</span>,
            },
            { field: 'Nom', headerName: 'Nom', width: 130 },
            { field: 'Prenom', headerName: 'Prénom', width: 130 },
            {
                field: 'Telephone', headerName: 'Téléphone', width: 130,
                renderCell: ({ value, row }) => <span>{value ?? pick(row, 'Phone', 'Mobile') ?? '—'}</span>,
            },
            {
                field: 'Email', headerName: 'E-mail', width: 170,
                renderCell: ({ value, row }) => <span>{value ?? pick(row, 'Mail') ?? '—'}</span>,
            },
            {
                field: 'NumeroIdentite', headerName: "N° identité", width: 130,
                renderCell: ({ value, row }) => <span>{value ?? pick(row, 'CNI', 'NumeroPiece') ?? '—'}</span>,
            },
            ...(hasStatut ? [{
                field: 'Statut', headerName: 'Statut', width: 90,
                renderCell: ({ value, row }: any) => <StatutClient value={value ?? row.IsActif} />,
            } as DataTableColumn] : []),
            {
                field: 'Actions', headerName: 'Actions', width: 110, sortable: false,
                renderCell: ({ row }) => (
                    <div className="flex items-center gap-1">
                        <IconButton
                            tooltip='Modifier le client'
                            color='primary'
                            size={30}
                            onClick={(e) => { e.stopPropagation(); openEdit(row) }}
                        >
                            <Pencil size={15} />
                        </IconButton>
                        <IconButton
                            tooltip='Réinitialiser le mot de passe'
                            color='warning'
                            size={30}
                            onClick={(e) => { e.stopPropagation(); setConfirmReset({ open: true, row }) }}
                        >
                            <KeyRound size={15} />
                        </IconButton>
                    </div>
                ),
            },
        ];

    return (
        <Spin spinning={loading} >
            <PageHeader
                icon={<Users />}
                title="Gestion des clients"
                subtitle="Recherchez, créez et administrez les comptes clients"
                actions={
                    <Button
                        size='sm'
                        variant="primary"
                        startIcon={<Plus size={16} />}
                        onClick={openCreate}
                    >Nouveau client</Button>
                }
            />

            <div className="eb-table-wrap">
                <SearchInput
                    onSearch={(v) => SearchClient(v)}
                    size='lg'
                    className="mb-3"
                    placeholder='Rechercher un client par téléphone ou code'
                />
                {data.length > 0 ? (
                    <DataTable
                        onRowClick={(row: any) => setSelected(row)}
                        rows={data}
                        height={380}
                        columns={columns}
                        searchable
                        emptyText="Aucun client"
                    />
                ) : (
                    <div className="eb-card">
                        <Empty
                            icon={<Users size={44} strokeWidth={1.2} />}
                            description={searched && !searchRequired
                                ? 'Aucun client trouvé pour ce critère'
                                : 'Recherchez un client par téléphone ou code'}
                        />
                    </div>
                )}
            </div>
            <TableStatusBar count={data.length} loading={loading} onRefresh={() => SearchClient(critere)} />
            <InfoBar data={[
                { label: 'Client', value: selected ? `${pick(selected, 'Nom') || ''} ${pick(selected, 'Prenom') || ''}`.trim() || pick(selected, 'CodeClient', 'Id') : undefined },
                { label: 'Téléphone', value: pick(selected, 'Telephone', 'Phone', 'Mobile') },
                { label: 'Créé le', value: pick(selected, 'DateCreated', 'DateCreation') },
                { label: 'Créé par', value: pick(selected, 'CreatedBy') },
            ]} />

            {/* Drawer création / modification */}
            <FormDrawer
                open={open}
                onClose={() => setOpen(false)}
                title={client ? `Modification du client ${pick(client, 'Nom') || ''}` : 'Nouveau client'}
                hint={client
                    ? "Les informations existantes sont pré-remplies. Modifiez les champs souhaités puis enregistrez."
                    : "Renseignez les informations du nouveau client. Le mot de passe est optionnel."}
            >
                <FormClient client={client} onSaved={() => { setOpen(false); SearchClient(critere) }} />
            </FormDrawer>

            {/* Confirmation réinitialisation mot de passe */}
            <Modal
                open={confirmReset.open}
                onClose={() => setConfirmReset({ open: false, row: null })}
                title="Réinitialiser le mot de passe"
                footer={
                    <>
                        <Button variant='outline' onClick={() => setConfirmReset({ open: false, row: null })}>Fermer</Button>
                        <Button
                            variant='warning'
                            loading={actionLoading}
                            onClick={ResetPassword}
                        >Confirmer</Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    Voulez-vous réinitialiser le mot de passe du client{' '}
                    <strong>{pick(confirmReset.row, 'Nom')} {pick(confirmReset.row, 'Prenom')}</strong>
                    {pick(confirmReset.row, 'Telephone', 'Phone', 'Mobile') ? (
                        <> (<strong>{pick(confirmReset.row, 'Telephone', 'Phone', 'Mobile')}</strong>)</>
                    ) : null} ?
                </p>
            </Modal>
        </Spin>
    );
}
